import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { followup } from '../../../../helper/api_url';
import { swal } from '../../../../helper/swal';
import { getUniqueDistricts } from '../EnquiryList/enquiryUtils';

export const useFollowUpList = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    // Default to today's date formatted as YYYY-MM-DD
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const day = currentDate.getDate().toString().padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;

    const [selectedDate, setSelectedDate] = useState(formattedDate);
    const [selectedDistrict, setSelectedDistrict] = useState('all');

    // Fetch follow-ups using TanStack Query
    const {
        data: allPendingFollowups = [],
        isLoading,
        isError,
        error
    } = useQuery({
        queryKey: ['followups', selectedDate],
        queryFn: () => followup.pending(selectedDate),
        staleTime: 20 * 60 * 1000,
        cacheTime: 20 * 60 * 1000,
        select: (data) => {
            const list = data?.data?.followup || [];
            return list.filter((item) => {
                const status = (item.status || item.enquiry?.status || '').toLowerCase();
                return status !== 'completed' && status !== 'deleted' && status !== 'not interested';
            });
        }
    });

    // Extract unique sorted districts
    const districtList = useMemo(() => {
        return getUniqueDistricts(allPendingFollowups);
    }, [allPendingFollowups]);

    // Filter followups by selected district
    const displayedFollowups = useMemo(() => {
        if (selectedDistrict === 'all') return allPendingFollowups;
        return allPendingFollowups.filter((item) => {
            const d = (item.enquiry?.district || item.district || '').trim().toLowerCase();
            return d === selectedDistrict.toLowerCase();
        });
    }, [allPendingFollowups, selectedDistrict]);

    // Unified single error handler
    useEffect(() => {
        if (isError && error) {
            swal.error(error.response?.data?.message || error.message || 'Error loading follow-ups');
        }
    }, [isError, error]);

    const handleFollowSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ['followups'] });
    };

    const handleReport = (row) => {
        const enq = row.enquiry || row;
        const reportData = {
            ...enq,
            followups: (enq.followups && enq.followups.length > 0) ? enq.followups : [row]
        };
        navigate('/report', { state: { data: reportData } });
    };

    return {
        followups: displayedFollowups,
        rawFollowups: allPendingFollowups,
        districtList,
        selectedDistrict,
        setSelectedDistrict,
        isLoading,
        selectedDate,
        setSelectedDate,
        formattedDate,
        handleFollowSuccess,
        handleReport
    };
};

export default useFollowUpList;
